/* eslint-disable consistent-return */
import { apiPaths, getApiPath, httpStatus } from '../../constants'
import { Api } from '../../middlewares'
import { Logger, LoggerTypes } from '../../utils'

// -- GET (list)
export const GET_FLOW_TASK_INTERACTION_VARIABLE_SUCCESS = 'GET_FLOW_TASK_INTERACTION_VARIABLE_SUCCESS'
export const GET_FLOW_TASK_INTERACTION_VARIABLE_FAILURE = 'GET_FLOW_TASK_INTERACTION_VARIABLE_FAILURE'

// -- CREATE
export const CREATE_FLOW_TASK_INTERACTION_VARIABLE_SUCCESS = 'CREATE_FLOW_TASK_INTERACTION_VARIABLE_SUCCESS'
export const CREATE_FLOW_TASK_INTERACTION_VARIABLE_FAILURE = 'CREATE_FLOW_TASK_INTERACTION_VARIABLE_FAILURE'

// -- UPDATE
export const UPDATE_FLOW_TASK_INTERACTION_VARIABLE_SUCCESS = 'UPDATE_FLOW_TASK_INTERACTION_VARIABLE_SUCCESS'
export const UPDATE_FLOW_TASK_INTERACTION_VARIABLE_FAILURE = 'UPDATE_FLOW_TASK_INTERACTION_VARIABLE_FAILURE'

// -- DELETE
export const DELETE_FLOW_TASK_INTERACTION_VARIABLE_SUCCESS = 'DELETE_FLOW_TASK_INTERACTION_VARIABLE_SUCCESS'
export const DELETE_FLOW_TASK_INTERACTION_VARIABLE_FAILURE = 'DELETE_FLOW_TASK_INTERACTION_VARIABLE_FAILURE'

// -- FLOW_TASK_INTERACTION CACHE
export const CACHE_CREATE_FLOW_TASK_INTERACTION_VARIABLE = 'CACHE_CREATE_FLOW_TASK_INTERACTION_VARIABLE'
export const CACHE_UPDATE_FLOW_TASK_INTERACTION_VARIABLE = 'CACHE_UPDATE_FLOW_TASK_INTERACTION_VARIABLE'
export const CACHE_DELETE_FLOW_TASK_INTERACTION_VARIABLE = 'CACHE_DELETE_FLOW_TASK_INTERACTION_VARIABLE'
// ____________________________________________________________

/**
 * GET the task interaction variable broadcast two types of events:
 * - GET_FLOW_TASK_INTERACTION_VARIABLE_SUCCESS: in case of success.
 * - GET_FLOW_TASK_INTERACTION_VARIABLE_FAILURE: in case of failure.
 *
 * @param {UUID} pid Project Id UUID.
 * @param {UUID} sid Scenario Id UUID.
 * @param {UUID} fid FlowGroup Id UUID.
 * @param {UUID} tid Flow Task UUID.
 * @returns {Promise<object>}
 */
export const getFlowTaskInteractionVariables = (pid, sid, fid, tid) => async (dispatch) => {
  const path = getApiPath(apiPaths.FLOW_TASK_INTERACTION_VARIABLE_LIST, { pid, sid, fid, tid })
  const api = new Api(path)
  const result = await api.get()

  Logger.trace(LoggerTypes.ACTION, '[ACTION] flowTaskInteractionVariables.getFlowTaskInteractionVariables()', {
    path,
    result
  })

  if (result.status >= httpStatus.OK && result.status < httpStatus.AMBIGUOUS) {
    return dispatch({
      type: GET_FLOW_TASK_INTERACTION_VARIABLE_SUCCESS,
      data: result.data.items || []
    })
  }
  return dispatch({
    type: GET_FLOW_TASK_INTERACTION_VARIABLE_FAILURE,
    data: result.data
  })
}

/**
 * Create a new task interaction variable broadcast two types of events:
 * - CREATE_FLOW_TASK_INTERACTION_VARIABLE_SUCCESS: in case of success.
 * - CREATE_FLOW_TASK_INTERACTION_VARIABLE_FAILURE: in case of failure.
 *
 * @param {UUID} pid Project Id UUID.
 * @param {UUID} sid Scenario Id UUID.
 * @param {UUID} fid FlowGroup Id UUID.
 * @param {UUID} tid Flow Task UUID.
 * @param {object} interaction Variable interaction to create
 * @returns {Promise<object>}
 */
export const createFlowTaskInteractionVariable = (pid, sid, fid, tid, interaction) => async (dispatch) => {
  if (!interaction) {
    return
  }

  const path = getApiPath(apiPaths.FLOW_TASK_INTERACTION_VARIABLE_CREATE, { pid, sid, fid, tid })
  const api = new Api(path)
  const result = await api.create(interaction)

  Logger.trace(LoggerTypes.ACTION, '[ACTION] flowTaskInteractionVariables.createFlowTaskInteractionVariable()', {
    path,
    result
  })

  if (result.status >= httpStatus.OK && result.status < httpStatus.AMBIGUOUS) {
    return dispatch({
      type: CREATE_FLOW_TASK_INTERACTION_VARIABLE_SUCCESS,
      data: result.data
    })
  }
  return dispatch({
    type: CREATE_FLOW_TASK_INTERACTION_VARIABLE_FAILURE,
    data: result.data
  })
}

/**
 * Assign a Flow Task Interaction to the current flowTask, broadcast two types of events:
 * - UPDATE_FLOW_TASK_INTERACTION_VARIABLE_SUCCESS: in case of success.
 * - UPDATE_FLOW_TASK_INTERACTION_VARIABLE_FAILURE: in case of failure.
 *
 * @param {UUID} pid Project Id UUID.
 * @param {UUID} sid Scenario Id UUID.
 * @param {UUID} fid FlowGroup Id UUID.
 * @param {UUID} tid Flow Task UUID.
 * @param {Array<object>} interaction Array of interaction object to add to the current Flow Task.
 * @returns {Promise<object>}
 */
export const updateFlowTaskInteractionVariable = (pid, sid, fid, tid, interaction) => async (dispatch) => {
  if (!interaction) {
    return
  }

  const path = getApiPath(apiPaths.FLOW_TASK_INTERACTION_VARIABLE_UPDATE, {
    pid,
    sid,
    fid,
    tid,
    vid: interaction.id
  })
  const api = new Api(path)
  const result = await api.update(interaction)

  Logger.trace(LoggerTypes.ACTION, '[ACTION] flowTaskInteractionVariables.updateFlowTaskInteractionVariable()', {
    path,
    interaction
  })

  if (result.status >= httpStatus.OK && result.status < httpStatus.AMBIGUOUS) {
    return dispatch({
      type: UPDATE_FLOW_TASK_INTERACTION_VARIABLE_SUCCESS,
      data: result.data
    })
  }
  return dispatch({
    type: UPDATE_FLOW_TASK_INTERACTION_VARIABLE_FAILURE,
    data: result.data
  })
}

/**
 * Delete a flowGroup and broadcast two types of events:
 * - DELETE_FLOW_TASK_INTERACTION_VARIABLE_SUCCESS: in case of success.
 * - DELETE_FLOW_TASK_INTERACTION_VARIABLE_FAILURE: in case of failure.
 *
 * @param {UUID} pid Project Id UUID.
 * @param {UUID} sid Scenario Id UUID.
 * @param {UUID} fid FlowGroup Id to delete.
 * @param {UUID} tid Flow Task UUID.
 * @param {UUID} vid Flow Task variable Id UUID.
 * @returns {Promise<object>}
 */
export const deleteFlowTaskInteractionVariable = (pid, sid, fid, tid, vid) => async (dispatch) => {
  const path = getApiPath(apiPaths.FLOW_TASK_INTERACTION_VARIABLE_DELETE, { pid, sid, fid, tid, vid })
  const api = new Api(path)
  const result = await api.delete()

  Logger.trace(LoggerTypes.ACTION, '[ACTION] flowTaskInteractionVariables.deleteFlowTaskInteractionVariable()', {
    path,
    result
  })

  if (result.status >= httpStatus.OK && result.status < httpStatus.AMBIGUOUS) {
    return dispatch({
      type: DELETE_FLOW_TASK_INTERACTION_VARIABLE_SUCCESS,
      data: fid
    })
  }
  return dispatch({
    type: DELETE_FLOW_TASK_INTERACTION_VARIABLE_FAILURE,
    data: result.data
  })
}

/**
 * Add the definition of a task instance interaction in the flowGroup's cache.
 *
 * @param {object} flowGroup the flow group of the task
 * @param {object} tid the identifier of the task instance to be updated
 * @param {object} interactions the array of interactions to be updated
 * @returns {object}
 */
export const cacheCreateFlowTaskInteractionVariable = (flowGroup, tid, interactions) => ({
  type: CACHE_CREATE_FLOW_TASK_INTERACTION_VARIABLE,
  data: {
    flowGroup,
    tid,
    interactions
  }
})

/**
 * Update the definition of a task instance interaction in the flowGroup's cache.
 *
 * @param {object} flowGroup the flow group of the task
 * @param {object} tid the identifier of the task instance to be updated
 * @param {object} interaction the interaction to be updated
 * @returns {object}
 */
export const cacheUpdateFlowTaskInteractionVariable = (flowGroup, tid, interaction) => ({
  type: CACHE_UPDATE_FLOW_TASK_INTERACTION_VARIABLE,
  data: {
    flowGroup,
    tid,
    interaction
  }
})

/**
 * Remove the definition of a task instance interaction in the flowGroup's cache.
 *
 * @param {object} flowGroup the flow group of the task
 * @param {object} tid the identifier of the task instance to be updated
 * @param {object} interaction the interaction to be removed
 * @returns {object}
 */
export const cacheDeleteFlowTaskInteractionVariable = (flowGroup, tid, interaction) => ({
  type: CACHE_DELETE_FLOW_TASK_INTERACTION_VARIABLE,
  data: {
    flowGroup,
    tid,
    interaction
  }
})
